import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

  const handleChange = event => setForm(value => ({ ...value, [event.target.name]: event.target.value }));

  const handleSubmit = async event => {
    event.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('https://formspree.io/f/xpwpqdvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '15px 16px',
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: 'var(--white)',
    fontSize: 15,
    fontFamily: 'var(--font-body)',
    outline: 'none',
    display: 'block',
  };

  const contactInfo = [
    { code: 'EM', label: 'Email', value: 'prakash12991299@gmail.com', link: 'mailto:prakash12991299@gmail.com', accent: 'var(--aqua)' },
    { code: 'IN', label: 'Location', value: 'Chennai, Tamil Nadu, India', accent: 'var(--rose)' },
    { code: 'WEB', label: 'Website', value: 'prakash1210.site', link: 'https://prakash1210.site', accent: 'var(--sun)' },
  ];

  return (
    <section id="contact" ref={ref} className="section-pad alt">
      <div className="ghost-text">CONTACT</div>

      <div className="section-shell">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 58 }}
        >
          <div className="eyebrow" style={{ justifyContent: 'center' }}>Get in touch</div>
          <h2 className="section-title">Ready to make your project look premium?</h2>
        </motion.div>

        <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.15fr', gap: 24, alignItems: 'stretch' }}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="premium-card"
            style={{ padding: 30 }}
          >
            <div className="color-line" style={{ marginBottom: 28 }} />
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, lineHeight: 1.05, marginBottom: 16 }}>
              Send the idea. I will shape the screen.
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.8, marginBottom: 34 }}>
              Tell me about your website, app interface, business page, or redesign. I will respond with a clear next step.
            </p>

            <div style={{ display: 'grid', gap: 14 }}>
              {contactInfo.map(item => (
                <div key={item.label} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      display: 'grid',
                      placeItems: 'center',
                      background: `linear-gradient(135deg, ${item.accent}, rgba(255,255,255,0.1))`,
                      color: 'var(--ink)',
                      fontFamily: 'var(--font-display)',
                      fontSize: 12,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {item.code}
                  </div>
                  <div>
                    <div style={{ color: 'var(--muted)', fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 1.3, textTransform: 'uppercase', fontWeight: 800 }}>
                      {item.label}
                    </div>
                    {item.link ? (
                      <a href={item.link} style={{ color: 'var(--white)', textDecoration: 'none', fontWeight: 700, wordBreak: 'break-word' }}>{item.value}</a>
                    ) : (
                      <span style={{ color: 'var(--white)', fontWeight: 700 }}>{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="premium-card"
            style={{ padding: 30 }}
          >
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              {['name', 'email'].map(field => (
                <label key={field} style={{ display: 'block' }}>
                  <span style={{ display: 'block', color: 'var(--aqua)', fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 }}>
                    {field === 'name' ? 'Your name' : 'Your email'}
                  </span>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    required
                    placeholder={field === 'name' ? 'Prakash C' : 'prakash@example.com'}
                    style={inputStyle}
                  />
                </label>
              ))}
            </div>

            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={{ display: 'block', color: 'var(--rose)', fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 }}>
                Subject
              </span>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                required
                placeholder="Project Inquiry"
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 22 }}>
              <span style={{ display: 'block', color: 'var(--sun)', fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 }}>
                Message
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                placeholder="Tell me about your project..."
                rows={6}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </label>

            <button type="submit" disabled={status === 'sending'} className="btn-primary" style={{ width: '100%', opacity: status === 'sending' ? 0.72 : 1 }}>
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>

            {status === 'success' && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 16, color: 'var(--lime)', fontWeight: 700 }}>
                Message sent successfully. I will get back to you soon.
              </motion.p>
            )}
            {status === 'error' && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 16, color: 'var(--rose)', fontWeight: 700 }}>
                Something went wrong. Please email me directly.
              </motion.p>
            )}
          </motion.form>
        </div>
      </div>

      <style>{`
        input:focus, textarea:focus { border-color: var(--aqua) !important; }
        @media (max-width: 920px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
